import React, { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Eye, EyeOff, VolumeX, ShieldCheck, MailCheck, MailWarning, LogOut, ScrollText, ShieldAlert, Crown, CreditCard, CheckCircle2, FileText, Scale, Trash2, ChevronRight, Landmark, Gift, MessageSquareText, Star, Sparkles, RefreshCw, ExternalLink, Download, DatabaseBackup, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/apiClient";
import { PLAN_DISPLAY_NAME } from "@/lib/plans";
import { usePaywall } from "@/components/Paywall";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

function SubscriptionSection({ subscription, reloadSubscription, founder }) {
  const [busy, setBusy] = useState(false);
  const s = subscription || {};
  const status = founder ? "founder" : (s.status || "none");
  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/cancel", {});
      toast.success("Subscription cancelled");
      reloadSubscription && reloadSubscription();
      if (data.access_until) toast.message(`Access until ${fmtDate(data.access_until)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); }
  };
  const label = { founder: "Founding Member · Free forever", trialing: "Free trial active", active: "Active subscription", canceled: "Cancelled", none: "No active plan" }[status] || status;
  const planName = founder ? "Litigator Pro" : (PLAN_DISPLAY_NAME[s.plan] || "Basic Watch");
  return (
    <section className="border rounded-lg px-5 py-4" style={{ background: founder ? "rgba(46,130,246,0.06)" : "var(--ek-surface)", borderColor: founder ? "rgba(46,130,246,0.3)" : "var(--ek-border)" }} data-testid="subscription-section">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {founder ? <Crown className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> : <CreditCard className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />}
          <div>
            <div className="text-sm font-medium" data-testid="plan-name">{planName} Plan</div>
            <div className="ek-mono-label mt-1">{label.toUpperCase()}</div>          </div>
        </div>
        {founder && <span className="ek-stamp" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.4)" }} data-testid="founder-badge">FOUNDER</span>}
      </div>
      {status === "trialing" && (
        <p className="text-sm mt-3" style={{ color: "var(--ek-text-secondary)" }} data-testid="trial-billing-note">
          {s.trial_end ? <>Your free trial ends on <b style={{ color: "var(--ek-text)" }}>{fmtDate(s.trial_end)}</b>. After that you'll be billed </> : <>When your free trial ends you'll be billed </>}
          <b style={{ color: "var(--ek-text)" }}>${(s.prices?.[s.plan] ?? (s.plan === "pro" ? 44.99 : 24.99)).toFixed(2)}/month</b>. Cancel before then and your card will not be charged.
        </p>
      )}
      {status === "canceled" && s.access_until && (
        <p className="text-sm mt-3 flex items-center gap-2" style={{ color: "var(--ek-secure)" }} data-testid="cancel-confirmation"><CheckCircle2 className="h-4 w-4" /> Subscription cancelled. You still have access until {fmtDate(s.access_until)}.</p>
      )}
      {!founder && (status === "trialing" || status === "active") && (
        <Button onClick={cancel} disabled={busy} data-testid="cancel-subscription-btn" variant="outline" className="rounded-md border mt-4" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-error)" }}>
          {busy ? "CANCELLING..." : "Cancel subscription"}
        </Button>
      )}
    </section>
  );
}

function CreditPacksSection({ lowBalance = false, remaining = 0 }) {
  const [packs, setPacks] = useState(null);
  const [busy, setBusy] = useState("");
  React.useEffect(() => {
    api.get("/billing/packs").then(({ data }) => setPacks(data)).catch((e) => console.error("load packs failed", e));
  }, []);
  const buy = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/billing/credits/checkout", { pack_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(""); }
  };
  if (!packs?.credit_packs) return null;
  const order = ["credits_100"];
  return (
    <section className="border rounded-lg px-5 py-4 space-y-3" style={{ background: "var(--ek-surface)", borderColor: lowBalance ? "rgba(245,158,11,0.5)" : "var(--ek-border)" }} data-testid="credit-packs-section">
      <div className="flex items-center gap-3">
        <Sparkles className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
        <div>
          <div className="text-sm font-medium">Buy KeeperCredits™</div>
          <div className="ek-mono-label mt-1">TOP UP · NEVER EXPIRE</div>
        </div>
      </div>
      {lowBalance && (
        <div className="rounded-md px-3 py-2 text-sm" style={{ background: "rgba(245,158,11,0.12)", color: "var(--ek-text)" }} data-testid="low-balance-nudge">
          <b>Running low</b> — {remaining} KeeperCredit{remaining === 1 ? "" : "s"} left. Refill now so your AI analyses never stall mid-case.
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        {order.filter((id) => packs.credit_packs[id]).map((id) => {
          const p = packs.credit_packs[id];
          return (
            <button key={id} onClick={() => buy(id)} disabled={!!busy} data-testid={`buy-pack-${id}`}
              className="rounded-md border p-3 text-left transition-colors min-h-[76px] disabled:opacity-60"
              style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              <div className="text-sm font-semibold" style={{ color: "var(--ek-text)" }}>{p.credits} credits</div>
              <div className="ek-mono-label mt-1">${p.price}</div>
              <div className="text-xs mt-1" style={{ color: "var(--ek-primary)" }}>{busy === id ? "Redirecting…" : "Buy"}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function RedeemSection({ reloadSubscription }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: code.trim() });
      toast.success(data.message || "Code redeemed!");
      setCode("");
      reloadSubscription && reloadSubscription();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not redeem code");
    } finally { setBusy(false); }
  };
  return (
    <section className="border rounded-lg px-5 py-4 space-y-3" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="redeem-section">
      <div className="flex items-center gap-3">
        <Gift className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
        <div>
          <div className="text-sm font-medium">Have a promo code?</div>
          <div className="ek-mono-label mt-1">UNLOCK LITIGATOR PRO ACCESS</div>
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. JUSTICE50" data-testid="promo-code-input" className="bg-transparent uppercase" />
        <Button onClick={redeem} disabled={busy || !code.trim()} data-testid="redeem-code-btn" className="rounded-md shrink-0" style={{ background: "var(--ek-primary)", color: "#fff" }}>
          {busy ? "…" : "Redeem"}
        </Button>
      </div>
    </section>
  );
}

function ReviewBanner({ onShare, onDismiss }) {
  return (
    <section className="border rounded-lg px-5 py-4" style={{ background: "rgba(46,130,246,0.08)", borderColor: "rgba(46,130,246,0.35)" }} data-testid="review-prompt-banner">
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--ek-primary)" }} />
        <div className="flex-1">
          <p className="text-sm" style={{ color: "var(--ek-text)" }}>Hope your documentation is going well! Justice would love to hear how we've helped your case so far.</p>
          <div className="flex gap-2 mt-3">
            <Button onClick={onShare} data-testid="review-share-btn" className="rounded-md h-9" style={{ background: "var(--ek-primary)", color: "#fff" }}>Share feedback</Button>
            <Button onClick={onDismiss} data-testid="review-dismiss-btn" variant="outline" className="rounded-md h-9 border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>Maybe later</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

const FeedbackSection = React.forwardRef(function FeedbackSection(_props, ref) {
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [allowPublic, setAllowPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (message.trim().length < 3) { toast.message("Please share a little more detail"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", { message: message.trim(), rating: rating || null, allow_public: allowPublic });
      setDone(true); setMessage(""); setRating(0); setAllowPublic(false);
      toast.success("Thank you — Justice appreciates your feedback!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  return (
    <section ref={ref} className="border rounded-lg px-5 py-4 space-y-3" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="feedback-section">
      <div className="flex items-center gap-3">
        <MessageSquareText className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
        <div>
          <div className="text-sm font-medium">Share your feedback</div>
          <div className="ek-mono-label mt-1">HELP US IMPROVE EVIDENCEKEEPER</div>
        </div>
      </div>
      {done ? (
        <p className="text-sm flex items-center gap-2" style={{ color: "var(--ek-secure)" }} data-testid="feedback-thanks"><CheckCircle2 className="h-4 w-4" /> Thanks! Your feedback was sent.</p>
      ) : (
        <>
          <div className="flex items-center gap-1" data-testid="feedback-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rating-star-${n}`} aria-label={`${n} star`}>
                <Star className="h-5 w-5" style={{ color: n <= rating ? "var(--ek-warning)" : "var(--ek-steel)", fill: n <= rating ? "var(--ek-warning)" : "none" }} />
              </button>
            ))}
          </div>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="How has EvidenceKeeper helped your case?" data-testid="feedback-message-input" className="bg-transparent resize-none" />
          <label className="flex items-center gap-2 min-h-[40px] cursor-pointer">
            <Checkbox checked={allowPublic} onCheckedChange={(v) => setAllowPublic(!!v)} data-testid="feedback-public-checkbox" />
            <span className="text-xs" style={{ color: "var(--ek-text-secondary)" }}>EvidenceKeeper may share this as an anonymous testimonial</span>
          </label>
          <Button onClick={submit} disabled={busy} data-testid="submit-feedback-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "SENDING…" : "Submit feedback"}
          </Button>
        </>
      )}
    </section>
  );
});


function ExportDataSection() {
  const [job, setJob] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const timer = React.useRef(null);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/account/export");
      setJob(data);
      return data;
    } catch (e) { console.error("export status failed", e); return null; }
  }, []);

  React.useEffect(() => {
    refresh();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [refresh]);

  React.useEffect(() => {
    const active = job && (job.status === "queued" || job.status === "running");
    if (active && !timer.current) {
      timer.current = setInterval(refresh, 2000);
    } else if (!active && timer.current) {
      clearInterval(timer.current); timer.current = null;
    }
  }, [job, refresh]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/export", {});
      setJob(data);
      toast.success("Preparing your data export — you can leave this screen.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start export"); }
    finally { setBusy(false); }
  };

  const running = job && (job.status === "queued" || job.status === "running");
  const ready = job && job.status === "completed";

  return (
    <section className="border rounded-lg px-5 py-4 space-y-3" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="export-data-section">
      <div className="flex items-center gap-3">
        <DatabaseBackup className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
        <div>
          <div className="text-sm font-medium">Export My Data</div>
          <div className="ek-mono-label mt-1">DOWNLOAD EVERYTHING BEFORE YOU LEAVE</div>
        </div>
      </div>
      <p className="text-xs leading-relaxed" style={{ color: "var(--ek-text-muted)" }}>
        Get one archive with your original evidence files, your case records (timeline, financial, communications, court orders), and every report you've generated. We build it in the background and give you a download link when it's ready.
      </p>

      {running && (
        <div className="rounded-md border p-3" style={{ borderColor: "rgba(46,130,246,0.3)", background: "var(--ek-page)" }} data-testid="export-data-progress">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--ek-primary)" }} />
            <span className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>{job.stage_label || "Preparing…"}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ek-surface)" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${job.progress || 0}%`, background: "var(--ek-primary)" }} />
          </div>
        </div>
      )}

      {ready && (
        <a href={`${BACKEND_URL}/api/account/export/download?token=${job.download_token}`} data-testid="export-data-download">
          <Button className="w-full rounded-md" style={{ background: "var(--ek-secure)", color: "#04121a" }}>
            <Download className="h-4 w-4 mr-2" /> Download my data{job.size_bytes ? ` (${(job.size_bytes / 1048576).toFixed(1)} MB)` : ""}
          </Button>
        </a>
      )}

      {!running && (
        <Button onClick={start} disabled={busy} variant="outline" data-testid="export-data-btn" className="w-full rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
          {busy ? "Starting…" : ready ? "Rebuild export" : "Prepare my data export"}
        </Button>
      )}
    </section>
  );
}

function PrivacyAISection() {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => {
    api.get("/privacy/consent").then(({ data }) => setData(data)).catch((e) => console.error("load consent failed", e));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const toggleAccount = async (v) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/privacy/consent", { ai_processing_consent: v });
      setData((prev) => ({ ...prev, ai_processing_consent: d.ai_processing_consent }));
      toast.success(v ? "AI processing enabled" : "AI processing turned off — manual features stay available");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  const toggleMatter = async (id, v) => {
    try {
      await api.post(`/matters/${id}/ai-toggle`, { enabled: v });
      setData((prev) => ({ ...prev, matters: prev.matters.map((m) => m.id === id ? { ...m, ai_enabled: v } : m) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update case"); }
  };

  const aiOn = !!data?.ai_processing_consent;
  return (
    <section className="border rounded-lg px-5 py-4 space-y-4" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="privacy-ai-section">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
        <div>
          <div className="text-sm font-medium">Privacy &amp; AI</div>
          <div className="ek-mono-label mt-1">YOU CONTROL WHAT JUSTICE PROCESSES</div>
        </div>
      </div>

      <p className="text-xs leading-relaxed" style={{ color: "var(--ek-text-muted)" }}>
        Your Evidence Vault stores and encrypts your files (AES-256, SHA-256 hashed). <b style={{ color: "var(--ek-text-secondary)" }}>Uploading evidence does not enable AI.</b> Turn on AI processing below to let Justice extract facts, dates, and summaries from the specific items you analyze. You can preserve evidence, build timelines, and export without AI.
      </p>

      <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-3" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
        <div>
          <div className="text-sm font-medium" style={{ color: "var(--ek-text)" }}>AI Processing (Justice)</div>
          <div className="ek-mono-label mt-1" style={{ color: aiOn ? "var(--ek-secure)" : "var(--ek-text-muted)" }}>{aiOn ? "ENABLED" : "OFF · MANUAL ONLY"}</div>
        </div>
        <Switch checked={aiOn} disabled={busy} onCheckedChange={toggleAccount} data-testid="ai-consent-toggle" aria-label="Enable AI processing" />
      </div>

      {aiOn && data?.matters?.length > 0 && (
        <div className="space-y-2">
          <div className="ek-mono-label">AI PER CASE</div>
          {data.matters.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-4 py-1.5">
              <span className="text-sm" data-private style={{ color: "var(--ek-text-secondary)" }}>{m.title}</span>
              <Switch checked={m.ai_enabled !== false} onCheckedChange={(v) => toggleMatter(m.id, v)} data-testid={`matter-ai-toggle-${m.id}`} aria-label={`AI for ${m.title}`} />
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px]" style={{ color: "var(--ek-text-muted)" }}>
        When AI is on, only the specific content you analyze is sent to our AI provider for that request. See our <Link to="/privacy" className="underline">Privacy Policy</Link>.
      </p>
    </section>
  );
}

function BillingComplianceSection({ reloadSubscription }) {
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/restore", {});
      const parts = [];
      if (data.subscription) parts.push("subscription synced");
      if (data.one_time) parts.push(`${data.one_time} purchase(s) restored`);
      toast.success(parts.length ? `Restored: ${parts.join(", ")}` : "Your purchases are already up to date.");
      reloadSubscription && reloadSubscription();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore purchases"); }
    finally { setBusy(false); }
  };
  return (
    <section className="border rounded-lg px-5 divide-y" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="billing-compliance-section">
      <Row>
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
          <div>
            <div className="text-sm font-medium">Restore Purchases</div>
            <div className="ek-mono-label mt-1">RE-SYNC YOUR SUBSCRIPTION & PASSES</div>
          </div>
        </div>
        <Button onClick={restore} disabled={busy} data-testid="restore-purchases-btn" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
          {busy ? "Restoring…" : "Restore"}
        </Button>
      </Row>
      <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener noreferrer" data-testid="manage-subscription-link">
        <Row>
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5" style={{ color: "var(--ek-steel)" }} />
            <div>
              <div className="text-sm font-medium">Manage Subscription</div>
              <div className="ek-mono-label mt-1">APPLE USERS · MANAGE IN APP STORE</div>
            </div>
          </div>
          <ExternalLink className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} />
        </Row>
      </a>
    </section>
  );
}

function fmt(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

const ACTION_LABEL = {
  PIN_SET: "Vault PIN configured",
  VAULT_UNLOCK: "Evidence Locker unlocked",
  PDF_EXPORT: "PDF export authorized",
};

function Row({ children }) {
  return <div className="flex items-center justify-between gap-4 py-4">{children}</div>;
}

export default function More({ user, audit, subscription, reloadSubscription, onSetupPin, privacy, setPrivacy, onQuiet, onResendVerification, logout, onNav, proAccess }) {
  const feedbackRef = React.useRef(null);
  const { openPaywall } = usePaywall();
  const scrollToFeedback = () => feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  const openDamages = () => {
    if (proAccess) { onNav && onNav("damages"); return; }
    openPaywall({
      feature: "Damages Ledger",
      reason: "Itemize financial damages with a court-ready Statement of Damages. Available on Litigator Pro or with a Case Prep Pass.",
      unlock: "pro",
    });
  };
  const dismissReview = async () => {
    try { await api.post("/feedback/dismiss", {}); reloadSubscription && reloadSubscription(); } catch { /* ignore */ }
  };
  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="more-view">
      <div>
        <div className="ek-stamp inline-block mb-3">SETTINGS</div>
        <h2 className="font-head text-2xl font-semibold tracking-tight">More</h2>
      </div>

      {subscription?.review_prompt && <ReviewBanner onShare={scrollToFeedback} onDismiss={dismissReview} />}

      {/* Litigator Pro tools */}
      <section className="border rounded-lg px-5" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="pro-tools-section">
        <button onClick={openDamages} data-testid="more-damages-ledger" className="w-full text-left">
          <Row>
            <div className="flex items-center gap-3">
              <Landmark className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  Damages Ledger
                  <span className="ek-stamp" data-testid="damages-pro-badge" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.45)" }}>PRO</span>
                </div>
                <div className="ek-mono-label mt-1">{proAccess ? "ITEMIZED FINANCIAL DAMAGES" : "LITIGATOR PRO FEATURE"}</div>
              </div>
            </div>
            <ChevronRight className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} />
          </Row>
        </button>
      </section>

      {/* Account */}
      <section className="border rounded-lg px-5 divide-y" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <Row>
          <div>
            <div className="text-sm font-medium" data-private>{user.email}</div>
            <div className="ek-mono-label mt-1">{user.role?.toUpperCase()} ACCOUNT</div>
          </div>
          {user.email_verified ? (
            <span className="flex items-center gap-1.5 text-sm" style={{ color: "var(--ek-secure)" }}><MailCheck className="h-4 w-4" /> Verified</span>
          ) : (
            <button onClick={onResendVerification} data-testid="more-resend-verification" className="flex items-center gap-1.5 text-sm underline" style={{ color: "var(--ek-warning)" }}><MailWarning className="h-4 w-4" /> Verify</button>
          )}
        </Row>
      </section>

      <SubscriptionSection subscription={subscription} reloadSubscription={reloadSubscription} founder={!!user.founding_member} />

      <BillingComplianceSection reloadSubscription={reloadSubscription} />

      {!user.founding_member && !subscription?.litigator_pro && (
        <RedeemSection reloadSubscription={reloadSubscription} />
      )}

      {subscription?.ai_usage && (
        <section className="border rounded-lg px-5 py-4" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="ai-usage-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <ScrollText className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
              <div>
                <div className="text-sm font-medium">KeeperCredits™</div>
                <div className="ek-mono-label mt-1">{subscription.ai_usage.tier.toUpperCase()} TIER</div>
              </div>
            </div>
            <span className="text-sm text-right" data-testid="ai-remaining">
              <b style={{ color: "var(--ek-primary)" }}>{subscription.ai_usage.remaining}</b>
              <span style={{ color: "var(--ek-text-muted)" }}> total remaining</span>
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--ek-page)" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (subscription.ai_usage.used / subscription.ai_usage.allowance) * 100)}%`, background: "var(--ek-primary)" }} />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "var(--ek-text-secondary)" }}>
            <span data-testid="ai-subscription-remaining"><b style={{ color: "var(--ek-text)" }}>{subscription.ai_usage.subscription_remaining ?? subscription.ai_usage.remaining}</b> / {subscription.ai_usage.allowance} monthly</span>
            <span data-testid="ai-purchased-balance"><b style={{ color: "var(--ek-text)" }}>{subscription.ai_usage.purchased ?? 0}</b> purchased (never expire)</span>
          </div>
          <p className="ek-mono-label mt-2">MONTHLY CREDITS SPENT FIRST · PURCHASED CREDITS ROLL OVER</p>
        </section>
      )}

      <CreditPacksSection lowBalance={(subscription?.ai_usage?.remaining ?? 999) <= 10} remaining={subscription?.ai_usage?.remaining ?? 0} />

      <PrivacyAISection />

      {/* Security */}
      <section className="border rounded-lg px-5 divide-y" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <Row>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
            <div>
              <div className="text-sm font-medium">Vault PIN</div>
              <div className="ek-mono-label mt-1">{user.has_vault_pin ? "CONFIGURED" : "NOT SET"}</div>
            </div>
          </div>
          <Button onClick={onSetupPin} variant="outline" data-testid="more-setup-pin" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <KeyRound className="h-4 w-4 mr-2" /> {user.has_vault_pin ? "Change PIN" : "Set PIN"}
          </Button>
        </Row>
        <Row>
          <div className="flex items-center gap-3">
            {privacy ? <EyeOff className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> : <Eye className="h-5 w-5" style={{ color: "var(--ek-steel)" }} />}
            <div>
              <div className="text-sm font-medium">Privacy Mode</div>
              <div className="ek-mono-label mt-1">HIDE SENSITIVE TEXT</div>
            </div>
          </div>
          <Button onClick={() => setPrivacy(!privacy)} variant="outline" data-testid="more-privacy-toggle" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: privacy ? "var(--ek-surface-hover)" : "transparent" }}>
            {privacy ? "On" : "Off"}
          </Button>
        </Row>
        <Row>
          <div className="flex items-center gap-3">
            <VolumeX className="h-5 w-5" style={{ color: "var(--ek-steel)" }} />
            <div>
              <div className="text-sm font-medium">Quiet Mode</div>
              <div className="ek-mono-label mt-1">MUTE AUDIO & DIM SCREEN</div>
            </div>
          </div>
          <Button onClick={onQuiet} variant="outline" data-testid="more-quiet-btn" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            Activate
          </Button>
        </Row>
      </section>

      {/* Audit trail */}
      <section className="border rounded-lg" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: "var(--ek-border)" }}>
          <ScrollText className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
          <span className="font-head font-semibold tracking-tight">Audit Trail</span>
        </div>
        {(!audit || audit.length === 0) ? (
          <div className="px-5 py-6 text-sm text-center" style={{ color: "var(--ek-text-muted)" }}>No activity logged yet.</div>
        ) : (
          <div className="divide-y max-h-72 overflow-y-auto" style={{ borderColor: "var(--ek-border)" }} data-testid="audit-list">
            {audit.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-5 py-2.5">
                <span className="text-sm">{a.detail || ACTION_LABEL[a.action] || a.action}</span>
                <span className="ek-mono-label shrink-0">{fmt(a.at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="flex items-center gap-2 text-xs" style={{ color: "var(--ek-text-muted)" }}>
        <ShieldAlert className="h-3.5 w-3.5" /> Justice can help you document, but cannot provide legal advice.
      </div>

      {/* Legal */}
      <section className="border rounded-lg px-5 divide-y" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="legal-section">
        <Link to="/privacy" data-testid="more-privacy-link">
          <Row>
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-steel)" }} />
              <div className="text-sm font-medium">Privacy Policy</div>
            </div>
            <ChevronRight className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} />
          </Row>
        </Link>
        <Link to="/terms" data-testid="more-terms-link">
          <Row>
            <div className="flex items-center gap-3">
              <Scale className="h-5 w-5" style={{ color: "var(--ek-steel)" }} />
              <div className="text-sm font-medium">Terms of Service</div>
            </div>
            <ChevronRight className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} />
          </Row>
        </Link>
      </section>

      <FeedbackSection ref={feedbackRef} />

      <ExportDataSection />

      {/* Danger zone */}
      <section className="border rounded-lg px-5" style={{ background: "rgba(240,97,109,0.05)", borderColor: "rgba(240,97,109,0.25)" }} data-testid="danger-zone">
        <Link to="/delete-account" data-testid="more-delete-account-link">
          <Row>
            <div className="flex items-center gap-3">
              <Trash2 className="h-5 w-5" style={{ color: "var(--ek-error)" }} />
              <div>
                <div className="text-sm font-medium" style={{ color: "var(--ek-error)" }}>Delete account</div>
                <div className="ek-mono-label mt-1">PERMANENTLY ERASE ALL DATA</div>
              </div>
            </div>
            <ChevronRight className="h-4 w-4" style={{ color: "var(--ek-error)" }} />
          </Row>
        </Link>
      </section>

      <Button onClick={logout} data-testid="more-logout-btn" variant="outline" className="w-full rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-error)" }}>
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}
